import { useThemeContext } from "@kalyzee/kalyzee-ui";
import { useMeetingSelector } from "@kalyzee/kast-web-sdk";
import { ChatRef, CyclingQRCodeData, QRCodeData, useRefAutoUpdate } from "@kalyzee/kast-web-sdk/web-components";
import { Chatroom, ChatroomStatus, Meeting, MeetingSession, MeetingState } from "@kalyzee/kast-web-sdk/websocket";
import { debounce } from "lodash";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PageSlaveMode } from "../../components/navigation/PageContext";
import { SessionMode } from "../../contexts/appStore/hooks";
import { useMeetingUtilsContext } from "../../contexts/meetingUtils/context";
import { useMessagingBridgeManagerMasterContext, useMessagingBridgeManagerSlaveContext } from "../../contexts/messageBridgeManager";
import { MessagingBridgeManagerMasterClient } from "../../helpers/bridge/master";
import { useVideoCarousel } from "./videoCarousel";

export const DEFAULT_CAROUSEL_INTERVAL = 60000;
export const DEFAULT_CAROUSEL_DELAY = 5000;

export interface SlaveQRCodeData {
  display: boolean,
  data?: QRCodeData,
}

export const useMeetingMasterMessagingBridge = () => {
  const bridge = useMessagingBridgeManagerMasterContext();
  const bridgeRef = useRefAutoUpdate(bridge);

  const unmountedRef = useRef(false);

  const { containerAPI } = useMeetingUtilsContext();

  const [meeting, meetingRef] = useMeetingSelector(containerAPI, "meeting");
  const [session, sessionRef] = useMeetingSelector(containerAPI, "session");
  const [sessions, sessionsRef] = useMeetingSelector(containerAPI, "sessions");
  const [chatrooms, chatroomsRef] = useMeetingSelector(containerAPI, "chatrooms");
  
  const [chatAdapters] = useMeetingSelector(containerAPI, "chatAdapters");
  const [mainChatroom] = useMeetingSelector(containerAPI, "chatrooms", chatrooms => chatrooms?.[0]);

  const chatAdapter = useMemo(() => {
    if (!chatAdapters || !mainChatroom) return undefined;
    return chatAdapters[mainChatroom.id];
  }, [chatAdapters, mainChatroom])
  const chatAdapterRef = useRefAutoUpdate(chatAdapter);

  const themeCtx = useThemeContext();
  
  const getMessagingBridgeClients = (): MessagingBridgeManagerMasterClient<SessionMode.SLAVE, PageSlaveMode.MEETING>[] => {
    return bridgeRef.current?.getActiveClients(true).filter((c) => c.data?.mode === SessionMode.SLAVE && c.data?.page === PageSlaveMode.MEETING) ?? [];
  };
  

  const broadcastMeeting = useCallback(
    debounce(
      () => {
        // the debounce can cause this fn to be called after unmount
        // which overrides the undefined posting on unmount
        if (unmountedRef.current) return;

        const bridge = bridgeRef.current;

        const meeting = meetingRef.current;
        const session = sessionRef.current;
        const sessions = sessionsRef.current;
        const chatrooms = chatroomsRef.current;

        if (meeting && meeting?.state !== MeetingState.STOPPED) {
          bridge?.postMeeting(meeting, session, sessions, chatrooms);
        } else {
          bridge?.postMeeting(undefined, undefined, undefined, undefined);
        }
      },
      100,
      { maxWait: 300 },
    ),
    [],
  );

  const broadcastChat = useCallback(
    debounce(
      () => {
        if (unmountedRef.current) return;
        const bridge = bridgeRef.current;
        if (!bridge) return;
        const adapter = chatAdapterRef.current;
        if (!adapter) return;
        const chatroom = chatroomsRef.current?.find((c) => c.id === adapter.getChatroomId());
        if (!chatroom) return;
        bridge.postChat(chatroom.id, adapter.getChatroomState() ?? chatroom.status, adapter.getMessages());
      },
      100,
      { maxWait: 300 },
    ),
    [],
  );

  const CarouselAPI = useVideoCarousel({
    meetingAPI: containerAPI,
    getClients: getMessagingBridgeClients,
    onCarousel: (data, delay, id) => {
      bridgeRef.current?.postDisplayCarousel(data, delay, id);
    },
  });
  const CarouselAPIRef = useRefAutoUpdate(CarouselAPI); 

  useEffect(() => {
    const onUnload = () => {
      bridgeRef.current?.postMeeting(undefined, undefined, undefined, undefined);
      CarouselAPIRef.current?.stop();
      unmountedRef.current = true;
    };
    window.addEventListener("beforeunload", onUnload);
    return () => {
      window.removeEventListener("beforeunload", onUnload);
    };
  }, []);

  useEffect(() => {
    if (!bridge) return;
    const getMeetingListener = bridge.addEventListener("requestMeeting", (_, respond) => {
      respond(meetingRef.current, sessionRef.current, sessionsRef.current, chatroomsRef.current);
    });

    const getChatListener = bridge.addEventListener("requestChat", (_, respond) => {
      console.log("Received request for chatroom:", _.content.chatroomId);
      const adapter = chatAdapterRef.current;
      if (!adapter) return;
      const chatroom = chatroomsRef.current?.find((c) => c.id === adapter.getChatroomId());
      if (!chatroom) return;
      respond(chatroom.id, adapter.getChatroomState() ?? chatroom.status, adapter.getMessages());
    });

    const clientDataListener = bridge.addEventListener("clientChange", () => {
      const newClients = getMessagingBridgeClients();
      if (newClients.length) {
        if (!CarouselAPI.isRunning()) CarouselAPI.start();
        else CarouselAPI.update();
      } else if (!newClients.length) {
        CarouselAPI.stop();
      }
    });

    return () => {
      getMeetingListener.flush();
      getChatListener.flush();
      clientDataListener.flush();
    };
  }, [bridge, CarouselAPI]);

  useEffect(() => {
    broadcastMeeting();
  }, [meeting, sessions, session, chatrooms]);

  useEffect(() => {
    CarouselAPI.update();
  }, [CarouselAPI.update]);

  useEffect(() => {
    if (!containerAPI) {
      CarouselAPI.stop();
      return;
    }
    if (!meeting) {
      CarouselAPI.stop();
    } else {
      const clients = getMessagingBridgeClients();
      if (!CarouselAPI.isRunning() && clients.length) {
        CarouselAPI.start();
      }
    }
    broadcastMeeting();
  }, [containerAPI, meeting, broadcastMeeting, CarouselAPI]);

  useEffect(() => {
    const chatCleanMessagesListener = chatAdapter?.addEventListener("cleanMessages", () => {
      broadcastChat();
    });
    const chatNewMessagesListener = chatAdapter?.addEventListener("newMessages", () => broadcastChat());
    const chatUpdateMessageListener = chatAdapter?.addEventListener("updateMessage", () => broadcastChat());
    const chatUpdateManyMessagesListener = chatAdapter?.addEventListener("updateMessages", () => broadcastChat());
    const chatStateListener = chatAdapter?.addEventListener("state", () => broadcastChat());
    return () => {
      chatCleanMessagesListener?.flush();
      chatNewMessagesListener?.flush();
      chatUpdateMessageListener?.flush();
      chatUpdateManyMessagesListener?.flush();
      chatStateListener?.flush();
    };
  }, [chatAdapter, broadcastChat]);

  useEffect(() => {
    if (!bridge) return;
    bridge.postTheme(themeCtx.preferredTheme);
  }, [bridge, themeCtx]);

  useEffect(() => {
    return () => {
      bridgeRef.current?.postMeeting(undefined, undefined, undefined, undefined);
      CarouselAPIRef.current?.stop();
      unmountedRef.current = true;
    };
  }, []);

  return bridge;
};

export const useMeetingSlaveMessagingBridge = (chatRef?: React.RefObject<ChatRef>) => {
  const bridge = useMessagingBridgeManagerSlaveContext();
  const bridgeRef = useRefAutoUpdate(bridge);

  const [meeting, setMeeting] = useState<Meeting>();
  const [session, setSession] = useState<MeetingSession>();
  const [sessions, setSessions] = useState<MeetingSession[]>();
  const [chatrooms, setChatrooms] = useState<Chatroom[]>();

  const [qrCodeData, setQRCodeData] = useState<CyclingQRCodeData>();
  const isInitiatedRef = useRef(false);
  const currentChatroomId = useRef<string>();
  const themeCtx = useThemeContext();

  useEffect(() => {
    if (!bridge) return;
    const readyListener = bridge.addEventListener("ready", () => {
      bridge.postRequestMeeting();
    });
    const meetingListener = bridge.addEventListener("meeting", (msg) => {
      const { meeting, session, sessions, chatrooms } = msg.content;
      setMeeting(meeting);
      setSession(session);
      setSessions(sessions);
      setChatrooms(chatrooms);
    });
    const chatListener = bridge.addEventListener("chat", (msg) => {
      console.log("Received chat update for chatroom:", msg.content.chatroomId);
      const { state, messages } = msg.content;
      if (state === ChatroomStatus.disabled) chatRef?.current?.disable();
      else chatRef?.current?.enable();
      chatRef?.current?.setMessages(
        messages.map((m) => {
          m.date = new Date(m.date); // we need to convert date to Date (date change to "string" type by passing by the websocket)
          return m;
        })
      );
    });

    const qrCodeListener = bridge.addEventListener("qrCodeData", (msg) => {
      const { data } = msg.content;
      setQRCodeData(data);
    });

    const themeListener = bridge.addEventListener("theme", (msg) => {
      const theme = msg.content;
      themeCtx.setPreferredTheme(theme);
    });

    const disconnectListener = bridge.addEventListener("disconnect", () => {
      setMeeting(undefined);
      setSession(undefined);
      setSessions(undefined);
      setChatrooms(undefined);
      setQRCodeData(undefined);
      currentChatroomId.current = undefined;
      bridge.postRequestMeeting();
    });

    if (!isInitiatedRef.current) {
      isInitiatedRef.current = true;
      bridge.postRequestMeeting();
    }

    return () => {
      readyListener.flush();
      meetingListener.flush();
      chatListener?.flush();
      disconnectListener.flush();
      qrCodeListener.flush();
      themeListener.flush();
    };
  }, [bridge, themeCtx]);

  useEffect(() => {
    const chatroomId = chatrooms?.[0]?.id;
    if (chatroomId && chatroomId !== currentChatroomId.current) {
      bridgeRef.current?.postRequestChat(chatroomId);
    }
    currentChatroomId.current = chatroomId;
  }, [chatrooms]);

  return { bridge, meeting, session, sessions, qrCodeData };
};
